/**
 * Capsule — the unsaved-work fingerprint.
 *
 * The same question the FlashBook studio asks, over the same kind of work: *has
 * anything actually changed since the last save?* Both the guard that stands
 * between the creator and a discarded capsule and the device-local copy that keeps
 * it depend on the answer, and both go wrong the instant it is guessed at.
 *
 * What is fingerprinted is the *record's* content, not the screen's. A slide's
 * `id` is included, deliberately: adding, deleting or reordering slides is real
 * work, and two capsules with the same three slides in a different order are not
 * the same capsule. What is *not* included is the picked cover film — a local file
 * the server has never seen — because the fingerprint is about what a save would
 * carry, and a save carries the film only once it is published. Including it would
 * mark a capsule dirty the moment its creator attached a film, and then keep it
 * dirty through every save. The film has its own, honest warning instead: a draft
 * does not carry it, and both the drafts list and the leave dialog say so.
 */
import {
  type BackgroundMusicChoice,
  musicChoiceToRecord,
} from '#/lib/backgroundMusic'
import {canonicalContent} from '#/lib/contentSignature'
import {type Slide} from '#/types/capsule'

export type CapsuleContentSignatureInput = {
  title: string
  description: string
  themeId: string
  /**
   * The creator's music choice: a track id, `null` for deliberate silence, or
   * absent when nothing was ever chosen.
   *
   * All three are folded in, and the two "no music" states are folded in
   * *differently*: choosing silence is work — the creator taking the bed away —
   * and a signature that could not tell it from "unchosen" would let that decision
   * be lost on the way out of the studio without the guard ever waking.
   */
  backgroundMusicId?: BackgroundMusicChoice
  /** Only present for capsules built from a custom HTML theme. */
  htmlThemeUrl?: string
  /** The content slides, as the record carries them (the cover is a field pair). */
  slides: Slide[]
}

/**
 * A string that changes if — and only if — the capsule's content changes.
 *
 * `htmlThemeUrl` is folded in with `?? ''` so an absent theme and an empty one are
 * the same state: a draft opened from a capsule that never had a custom theme must
 * not read as "you have unsaved changes" the moment it loads.
 */
export function capsuleContentSignature(
  input: CapsuleContentSignatureInput,
): string {
  return JSON.stringify(
    canonicalContent([
      input.title,
      input.description,
      input.themeId,
      musicChoiceToRecord(input.backgroundMusicId) ?? '',
      input.htmlThemeUrl ?? '',
      input.slides,
    ]),
  )
}
